import styled from '@emotion/styled';
import React from 'react';

import { PlanCode, billingPlans, getCurrencySymbol } from '@/features/billing';
import { Loader } from '@/features/common';
import { useWorkspaceId } from '@/hooks/router';
import { useGetInvoiceSummary } from '@/queries/subscriptions/queries';
import { format } from 'date-fns/format';

interface BillingSelectedPlanPriceConfirmationProps {
  plan?: PlanCode;
}

const formatDate = (date: number) => format(date, 'MMM d, yyyy');
const formatPrice = (price: number) => (price / 100).toFixed(2);

export function BillingSelectedPlanPriceConfirmation({
  plan,
}: BillingSelectedPlanPriceConfirmationProps) {
  const workspaceId = useWorkspaceId();
  const billingPlan = plan && billingPlans[plan];
  const { data: invoiceSummary } = useGetInvoiceSummary(
    Number(workspaceId),
    { subscriptionPlan: plan },
    !!plan
  );

  if (!billingPlan) return null;

  const billingPeriod = billingPlan.type === 'Annual' ? '1 year' : '1 month';
  const nextPaymentDate =
    billingPlan.type === 'Annual'
      ? formatDate(new Date().setFullYear(new Date().getFullYear() + 1))
      : formatDate(new Date().setMonth(new Date().getMonth() + 1));

  const formatAmmount = (price: number) =>
    `${getCurrencySymbol(invoiceSummary?.currency)}${formatPrice(price)}`;

  return (
    <PriceConfirmationContainer>
      <PurchaseInfo>
        You are subscribing for {billingPeriod}. Your next payment will be due{' '}
        {nextPaymentDate}. You can cancel anytime.
      </PurchaseInfo>

      <PriceInfo>
        {!invoiceSummary ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <>
            <Price>
              Total:
              {invoiceSummary.totalAmount !==
                invoiceSummary.totalAmountWithoutDiscount && (
                <span>
                  {formatAmmount(invoiceSummary.totalAmountWithoutDiscount)}
                </span>
              )}
              <span>{formatAmmount(invoiceSummary.totalAmount)}</span>
            </Price>

            {!!invoiceSummary.totalTaxAmount && (
              <Tax>
                Including {formatAmmount(invoiceSummary.totalTaxAmount)} VAT
              </Tax>
            )}
          </>
        )}
      </PriceInfo>
    </PriceConfirmationContainer>
  );
}

const PriceConfirmationContainer = styled.div`
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.purple[5]};
  display: grid;
  grid-template-columns: auto max-content;
  gap: 24px;
`;

const PurchaseInfo = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.typography.colorPrimary};
`;

const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const LoaderWrapper = styled.div`
  height: 40px;
  width: 40px;
`;

const Price = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.typography.colorPrimary};

  span:first-of-type:nth-last-of-type(2) {
    text-decoration: line-through;
  }
`;

const Tax = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.typography.colorSecondary};
`;
